import React, { useEffect, useCallback, useState, useRef } from 'react';
import { Input, Tooltip } from '@getro/rombo';
import { debounce } from 'debounce';
import { HelpCircle } from 'lucide-react';
import PropTypes from 'prop-types';
import { Box, Flex, Link, Text } from 'rebass/styled-components';

import { isBooleanSearch, isInvalidBooleanSearch } from '../../../helpers/booleanValidator';

const KeywordFilter = ({ value, onChange }) => {
  const [currentValue, setCurrentValue] = useState(value[0] || '');
  const [isWaiting, setIsWaiting] = useState(false);
  const [isInvalidBooleanSearchValue, setIsInvalidBooleanSearchValue] = useState(false);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Update currentValue when the filter value changes (e.g., AI filter)
  useEffect(() => {
    if (value[0] !== currentValue && !isWaiting) {
      setCurrentValue(value[0] || '');
    }
  }, [value, isWaiting]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnChange = useCallback(
    debounce((val) => {
      const isBooleanSearchValue = isBooleanSearch(val);

      setIsWaiting(false);

      if (isBooleanSearchValue && isInvalidBooleanSearch(val)) {
        setIsInvalidBooleanSearchValue(true);
        return;
      }

      onChangeRef.current(val.trim().length > 0 ? [val] : []);
      setIsInvalidBooleanSearchValue(false);
    }, 500),
    [],
  );

  useEffect(
    () => () => {
      debouncedOnChange.clear?.();
    },
    [debouncedOnChange],
  );

  const onCurrentChange = (event) => {
    setCurrentValue(event.target.value);
    setIsWaiting(true);
    debouncedOnChange(event.target.value);
  };

  useEffect(() => {
    const handleResetFilters = () => setCurrentValue('');
    window.addEventListener('resetFilters', handleResetFilters);

    return () => {
      window.removeEventListener('resetFilters', handleResetFilters);
    };
  }, []);

  return (
    <Flex
      sx={{
        input: {
          p: '8px',
          fontSize: '14px',
        },
        flexDirection: 'column',
      }}
    >
      <Flex
        sx={{
          flexDirection: 'column',
          gap: '4px',
          position: 'relative',
          ':hover': {
            '.keyword-filter-tooltip': { opacity: 1 },
          },
        }}
      >
        <Input
          data-testid="keyword-filter-input"
          placeholder="Keyword search"
          value={currentValue}
          onChange={onCurrentChange}
          sx={{
            height: '32px',
            pr: '28px!important',
          }}
        />
        <Box
          className="keyword-filter-tooltip"
          sx={{ position: 'absolute', right: '8px', top: '8px', opacity: 0, transition: 'opacity 300ms' }}
        >
          <Tooltip
            interactive
            position="right-start"
            width="320px"
            trigger={<Box as={HelpCircle} size="16px" sx={{ color: 'text.subtle', outline: 'none' }} />}
            content={
              <Flex flexDirection="column" sx={{ gap: '8px' }}>
                <Text fontSize="14px" color="neutral.0">
                  Search contacts by name, LinkedIn handle and tagline, current and past job titles, industry or skills.
                </Text>
                <Text fontSize="14px" color="neutral.0">
                  You can use the boolean operators AND, OR, NOT, quotes and parentheses.{' '}
                  <Link
                    fontSize="14px"
                    color="neutral.0"
                    href="https://help.getro.com/support/solutions/articles/65000188936-how-to-use-boolean-searches-to-find-contacts"
                    target="_blank"
                    sx={{ textDecoration: 'underline' }}
                  >
                    Learn more
                  </Link>
                  .
                </Text>
              </Flex>
            }
          />
        </Box>
        {isInvalidBooleanSearchValue && (
          <Text fontSize="12px" color="text.error">
            Invalid boolean format.{' '}
            <Link
              fontSize="12px"
              color="text.error"
              href="https://help.getro.com/support/solutions/articles/65000188936-how-to-use-boolean-searches-to-find-contacts"
              target="_blank"
              sx={{ textDecoration: 'underline' }}
            >
              Learn more
            </Link>
            .
          </Text>
        )}
      </Flex>
    </Flex>
  );
};

KeywordFilter.propTypes = {
  value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string])),
  onChange: PropTypes.func,
};

KeywordFilter.defaultProps = {
  value: [],
  onChange: () => {},
};

export default KeywordFilter;
